import {useState, useEffect} from 'react';
import {Box, Typography} from '@mui/material';
import './FilterPanel.scss';
import ArrFilterChip from '../../common/arr-filter-chip/ArrFilterChip';
import ArrButton from '../../common/arr-button/ArrButton';
import {t} from 'i18next';
import FilterSelectionModal, {
  FILTER_SELECTION_MAP,
} from '../../common/arr-filter-panel/filter-selection-modal/FilterSelectionModal';
import ArrSelectionChip, {
  ArrSelectionChipData,
} from '../../common/arr-selection-chip/ArrSelectionChip';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../shared/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {useDispatch} from 'react-redux';
import {useSearchParams} from 'react-router-dom';
import {clearComExplorerShowByFilters} from '../../../redux/slice/comExplorerSlice';

dayjs.extend(utc);

const topValueOptions: ISelectItem[] = [
  {id: '1', name: 'Top 8', value: '8', isDefault: true},
  {id: '2', name: 'Top 20', value: '20'},
  {id: '3', name: 'Top 50', value: '50'},
];

const categoryOptions: ISelectItem[] = [
  {id: '1', name: 'Domain', value: 'Domain', isDefault: true},
  {id: '2', name: 'Email Address', value: 'Emails'},
];

const volumeOptions: ISelectItem[] = [
  {id: '1', name: 'Exchange Volume', value: 'Exchange Volume', isDefault: true},
];

type FilterSelectionType =
  | 'include'
  | 'exclude'
  | 'before'
  | 'after'
  | 'on'
  | 'between'
  | 'below'
  | 'over'
  | 'at';

interface FilterValue {
  disabled: boolean;
  group: string | null;
  selected: boolean;
  text: string;
  value: string;
}

export interface ActiveFilter {
  id: string;
  type: 'NVARCHAR' | 'DATETIME' | 'INT';
  label: string;
  values?: FilterValue[];
  selectionType?: FilterSelectionType;
  separator?: string;
  startDate?: string;
  endDate?: string;
  isSelected?: boolean;
  startSize?: string;
  endSize?: string;
}

export interface FilterApplyPayload {
  activeFilters: ActiveFilter[];
  topValue: string | number | null;
  categoryValue: string | number | null;
  volumeValue: string | number | null;
  dataAsOf: string;
}

type FilterPanelProps = {
  title: string;
  filters: any[];
  onFilterApply: (payload: FilterApplyPayload) => void;
  displayShowBy?: boolean;
  disableApply?: boolean;
  resetSourceItems: () => void;
  selectedFilters?: any[] | null;
  dataAsOfProp: string;
  showValues?: {
    topValue: string | null;
    categoryValue: string | null;
    volumeValue: string | null;
  } | null;
  dataTestId:string;
};

function findSelectItem(
  items: ISelectItem[],
  selectedValue: string | undefined
): ISelectItem {
  return items.find((item) => String(item.value) === selectedValue) ?? items[0];
}

// =========FilterPanel Component=========

const FilterPanel: React.FC<FilterPanelProps> = ({
  title,
  filters,
  onFilterApply,
  displayShowBy = false,
  disableApply = false,
  resetSourceItems,
  selectedFilters = null,
  dataAsOfProp = '',
  showValues = null,
  dataTestId = 'arr-filter-tooltip',
}) => {
  const dispatch = useDispatch();
  const [parentFilters, setParentFilters] = useState<ActiveFilter[]>([]);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [isFilterUpdating, setIsFilterUpdating] = useState<boolean>(false);
  const [disableClear, setDisableClear] = useState(true);
 const [openDropdownFocusEl, setOpenDropdownFocusEl] =
    useState<HTMLElement | null>(null);
  const [topValue, setTopValue] = useState<ISelectItem | null>(
    topValueOptions[0]
  );
  const [categoryValue, setCategoryValue] = useState<ISelectItem | null>(
    categoryOptions[0]
  );
  const [volumeValue, setVolumeValue] = useState<ISelectItem | null>(
    volumeOptions[0]
  );
  const [now, setNow] = useState('');
  const [filterModalState, setFilterModalState] = useState<{
    anchorEl: HTMLElement | null;
    selectedFilter: ActiveFilter | null;
  }>({anchorEl: null, selectedFilter: null});

  useEffect(() => {
    const mappedFilters = filters.map((filter) => ({
      id: filter.projectFieldId,
      label: filter.projectFieldLabel,
      type: filter.dataType as 'NVARCHAR' | 'DATETIME',
      values: filter.values || [],
      isSelected: false,
    }));
    setParentFilters(mappedFilters);
  }, [filters]);
    useEffect(() => {
      function moveFocusOnCloseBtn(e: KeyboardEvent) {
        if (
          e.key === 'Tab' &&
          filterModalState.anchorEl &&
          !openDropdownFocusEl
        ) {
          e.preventDefault();
          (
            document.querySelector(
              '[data-testid="filter-selection-close-button"]'
            ) as HTMLElement
          )?.focus();
          setOpenDropdownFocusEl(filterModalState.anchorEl);
        }
      }
  
      window.addEventListener('keydown', moveFocusOnCloseBtn);
      return () => window.removeEventListener('keydown', moveFocusOnCloseBtn);
    }, [filterModalState.anchorEl, openDropdownFocusEl]);
  // New useEffect to update activeFilters when filterUpdate changes
  useEffect(() => {
    const literals = selectedFilters;
    if (
      !Array.isArray(literals) ||
      !Array.isArray(filters) ||
      filters.length === 0
    )
      return;

    // build base parentFilters from props.filters (same mapping as initial effect)
    const baseParents: ActiveFilter[] = filters.map((filter) => ({
      id: filter.projectFieldId,
      label: filter.projectFieldLabel,
      type: filter.dataType as 'NVARCHAR' | 'DATETIME',
      values: filter.values || [],
      isSelected: false,
    }));

    const toAdd: ActiveFilter[] = [];
    const updatedParents = baseParents.map((p) => {
      const match = literals.find(
        (l: any) =>
          String(l.fieldName ?? '').toLowerCase() ===
          String(p.label ?? '').toLowerCase()
      );
      if (match) {
        const isNotIn = match.literalValue?.startsWith('NOT IN');
        toAdd.push({
          id: match.fieldID,
          label: match.fieldName,
          type: match.fieldType,
          values: extractFilterValues(match.literalValue),
          selectionType: isNotIn ? 'exclude' : 'include',
          isSelected: true,
        } as ActiveFilter);
        return {...p, isSelected: true};
      }
      return p;
    });

    // update parent filters once
    setParentFilters(updatedParents);

    // merge new active filters without reading parentFilters (avoid loop)
    if (toAdd.length) {
      setActiveFilters((prev) => {
        const existingIds = new Set(prev.map((f) => f.id));
        const merged = [
          ...prev,
          ...toAdd.filter((f) => !existingIds.has(f.id)),
        ];
        return merged;
      });
    }
    if (toAdd.length == 0 && !selectedFilters?.length) {
      setActiveFilters(toAdd);
    }
  }, [selectedFilters, filters]);

  const extractFilterValues = (input: string): FilterValue[] => {
    const match = input.match(/\(([^()]*)\)/);
    if (!match) return [];

    const values = match[1]
      .split(',')
      .map((v) => v.trim().replace(/^N?'|'/g, ''));

    return values.map((v) => ({
      disabled: false,
      group: null,
      selected: true,
      text: v,
      value: v,
    }));
  };

  const getFilterInfo = (
    id: string,
    filterType: 'parent' | 'active'
  ): ActiveFilter => {
    if (filterType == 'parent') {
      const parentFilter =
        parentFilters.find((filter) => filter.id == id) ??
        ({} as ArrSelectionChipData);
      return {
        id: parentFilter.id,
        label: parentFilter.label,
        type: parentFilter.type,
        isSelected: parentFilter.isSelected,
      } as ActiveFilter;
    }
    return _.cloneDeep(
      activeFilters.find((filter) => filter.id == id) ?? ({} as ActiveFilter)
    );
  };

  const handleParentFilterClick = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    filterData: ArrSelectionChipData
  ) => {
    let targetElement: HTMLElement | null = null;

    if ('currentTarget' in event) {
      targetElement = event.currentTarget;
    }
    if (
      event.type === 'click' ||
      ('key' in event && (event.key === 'Enter' || event.code === 'Space'))
    ) {
      event.preventDefault();
      if (filterModalState.selectedFilter?.id == filterData.id) return;
      const flterInfo: ActiveFilter = getFilterInfo(filterData.id, 'parent');
      if (flterInfo.isSelected) return;
      setFilterModalState({
        anchorEl: targetElement,
        selectedFilter: {...flterInfo},
      });
    }
  };

  const activeFilterClickHandler = (
    element: HTMLElement | null,
    id?: string
  ) => {
    setIsFilterUpdating(true);
    if (element) {
      setFilterModalState({
        anchorEl: element,
        selectedFilter: {...getFilterInfo(id ?? '', 'active')},
      });
    }
  };

  const getActiveFiltersChip = () => {
    return activeFilters.map((filter) => {
      if (filter.type == 'NVARCHAR') {
        return (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? 'include'],
            ]}
            selections={
              filter.values?.map((value) => {
                return {id: value.text, value: `"${value.text}"`};
              }) ?? []
            }
            variant="v2"
            isResponsive={false}
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      } else if (filter.type == 'DATETIME') {
        let dateSelection = [
          {id: filter.startDate ?? '', value: filter.startDate ?? ''},
        ];
        if (filter.endDate) {
          dateSelection = [
            ...dateSelection,
            {id: filter.endDate ?? '', value: filter.endDate ?? ''},
          ];
        }
        return (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? ''],
            ]}
            selections={[...dateSelection]}
            variant="v2"
            isResponsive={false}
            operator="to"
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      } else if (filter.type == 'INT') {
        let sizeSelection = [
          {id: filter.startSize ?? '', value: filter.startSize ?? ''},
        ];
        if (filter.endSize) {
          sizeSelection = [
            ...sizeSelection,
            {id: filter.endSize ?? '', value: filter.endSize ?? ''},
          ];
        }
        return (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? ''],
            ]}
            selections={[...sizeSelection]}
            variant="v2"
            isResponsive={false}
            operator="to"
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      }
    });
  };


    const modalCloseHandler = () => {
      setFilterModalState({
        anchorEl: null,
        selectedFilter: null,
      });
      setIsFilterUpdating(false);
      setTimeout(() => {
        openDropdownFocusEl?.focus();
      }, 100);
      setOpenDropdownFocusEl(null);
    };
  const updateParentFilter = (id: string, isSelected: boolean) => {
    setIsFilterUpdating(true);
    setParentFilters((prev) => {
      return prev.map((filter) => {
        if (filter.id == id) {
          return {...filter, isSelected: isSelected};
        }
        return filter;
      });
    });
  };
  const onAddToFiltersHandler = (filterData: ActiveFilter) => {
    const existingActiveFilter = activeFilters.find(
      (filter) => filter.id == filterData.id
    );
    let updatedActiveFilters = activeFilters.map((filter) => {
      if (filter.id == filterData.id) return _.cloneDeep(filterData);
      return filter;
    });
    if (!existingActiveFilter) {
      updatedActiveFilters = [...updatedActiveFilters, _.cloneDeep(filterData)];
    }
    setActiveFilters([...updatedActiveFilters]);
    updateParentFilter(filterData.id, true);
    modalCloseHandler();
  };

  const clearActiveFilterHandler = (id?: string) => {
    if (id == filterModalState.selectedFilter?.id) {
      modalCloseHandler();
    }
    setActiveFilters((prev) => {
      const filterData = prev.filter((filter) => filter.id != id);
      return filterData;
    });
    setParentFilters((prev) => {
      return prev.map((filter) => {
        if (filter.id == id) {
          return {...filter, isSelected: false};
        }
        return {...filter};
      });
    });
  };

  const clearAllHandler = () => {
    setNow('');
    setTopValue(topValueOptions[0]);
    setCategoryValue(categoryOptions[0]);
    setVolumeValue(volumeOptions[0]);
    resetSourceItems();
    setActiveFilters([]);
    setParentFilters((prev) =>
      prev.map((filter) => ({...filter, isSelected: false}))
    );
    modalCloseHandler();
    dispatch(clearComExplorerShowByFilters());
  };

  const handleApplyFilters = () => {
    const formattedDate = dayjs.utc().format('YYYY/MM/DD HH:mm:ss [UTC]');
    setNow(formattedDate);
    onFilterApply({
      activeFilters,
      topValue: topValue?.value ?? null,
      categoryValue: categoryValue?.value ?? null,
      volumeValue: volumeValue?.value ?? null,
      dataAsOf: formattedDate,
    });
  };

  useEffect(() => {
    if (activeFilters?.length > 0) {
      setDisableClear(false);
    } else {
      setDisableClear(true);
    }
  }, [activeFilters]);

  const {communicationShowByFilters, sourceId: communicationSourceId} =
    useSelector((state: RootState) => state.comExplorer);

  useEffect(() => {
    if (
      communicationSourceId &&
      displayShowBy &&
      communicationShowByFilters?.categoryValue &&
      communicationShowByFilters?.topValue &&
      communicationShowByFilters?.volumeValue
    ) {
      setCategoryValue(
        findSelectItem(
          categoryOptions,
          communicationShowByFilters?.categoryValue?.toString()
        ) ?? categoryOptions[0]
      );
      setTopValue(
        findSelectItem(
          topValueOptions,
          communicationShowByFilters?.topValue?.toString()
        ) ?? topValueOptions[0]
      );
      setVolumeValue(
        findSelectItem(
          volumeOptions,
          communicationShowByFilters?.volumeValue?.toString()
        ) ?? volumeOptions[0]
      );
    }
  }, [communicationSourceId, communicationShowByFilters]);

  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('ReportID');

  useEffect(() => {
    if (!!dataAsOfProp && !!reportId) {
      const formattedDate = dayjs
        .utc(dataAsOfProp)
        .format('YYYY/MM/DD HH:mm:ss [UTC]');
      setNow(formattedDate);
    }
  }, [dataAsOfProp, reportId]);

  useEffect(() => {
    if (displayShowBy && !!reportId) {
      setCategoryValue(
        findSelectItem(
          categoryOptions,
          showValues?.categoryValue?.toString() ||
            communicationShowByFilters?.categoryValue?.toString()
        ) ?? categoryOptions[0]
      );
      setTopValue(
        findSelectItem(
          topValueOptions,
          showValues?.topValue?.toString() ||
            communicationShowByFilters?.topValue?.toString()
        ) ?? topValueOptions[0]
      );
      setVolumeValue(
        findSelectItem(
          volumeOptions,
          showValues?.volumeValue?.toString() ||
            communicationShowByFilters?.volumeValue?.toString()
        ) ?? volumeOptions[0]
      );
    }
  }, [displayShowBy, reportId, showValues]);

  return (
    <Box
      className="filter-panel"
      data-test-id="FilterPanel"
      role="region"
      aria-label={`${title} Filter Panel`}
    >
      <Box className="panel-header">
        <Typography
          className="title"
          variant="subtitle1"
          data-test-id="FilterPanelTitle"
          tabIndex={0}
          data-testid={`filter-panel-title`}
        >
          {title}
        </Typography>
        <ArrTooltipWrapper
          data-testid="ceh-tooltip-wrapper"
          title={t('docExplorer.filterAccordion.tooltip.title')}
          closeTooltipOnEscPress
          trigger={
            <div tabIndex={0} data-testid={`filter-panel-info-div`}>
              <InfoIcon
                fontSize="small"
                className="view-user-info-icon"
                data-testid={`ceh-info-icon-${dataTestId}`}
              />
            </div>
          }
        >
          <div data-testid={`ceh-tooltip-content-${dataTestId}`} tabIndex={0}>
            <p data-testid={`filter-panel-content`}>
              {t('docExplorer.filterAccordion.tooltip.content')}
            </p>
            <p data-testid={`filter-panel-content1`}>
              {t('docExplorer.filterAccordion.tooltip.content1')}
            </p>
          </div>
        </ArrTooltipWrapper>
      </Box>
      <Box className="filter-content">
        {/* Parent Filters Section */}
        <Box
          className="parent-filters"
          role="group"
          aria-label="Available Filters"
        >
          {parentFilters.map((filter) => (
            <ArrSelectionChip
              key={filter.id}
              aria-label={`Filter by ${filter.label}. ${filter.isSelected ? 'Currently active.' : 'Click to select.'}`}
              filterData={{...filter, isSelected: !!filter.isSelected}}
              onClick={handleParentFilterClick}
              tabIndex={-1}
              dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
            />
          ))}
        </Box>

        <Box className="active-filters">
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            paddingY={1}
            role="toolbar"
            aria-label="Filter Actions"
          >
            <Typography
              className="title"
              variant="subtitle1"
              data-testid={`filter-panel-active-filters`}
            >
              {t('docExplorer.filterAccordion.activeFilters')}
            </Typography>
            <Box>
              <ArrButton
                disabled={disableClear && disableApply}
                variant="text"
                size="medium"
                onClick={clearAllHandler}
                data-test-id="ClearAllFiltersButton"
                aria-label={t('common.button.clearAll') + ' active filters'}
                dataTestId="filter-panel-clear-all-button"
              >
                {t('common.button.clearAll')}
              </ArrButton>
              {!displayShowBy && (
                <ArrButton
                  size="medium"
                  disabled={disableApply}
                  onClick={handleApplyFilters}
                  data-test-id="ApplyFiltersButtonTop"
                  aria-label={t('common.applyFilters')}
                  dataTestId="filter-panel-apply-filters-button"
                >
                  {t('common.applyFilters')}
                </ArrButton>
              )}
            </Box>
          </Box>
          {/* Active Filters Chips Section */}
          <Box
            className="active-filters-chips"
            data-test-id="ActiveFiltersChipsContainer"
            role="status"
            aria-live="polite"
            pb={2}
          >
            {activeFilters.length > 0 && getActiveFiltersChip()}
          </Box>
          {/* Show By Section */}
          {displayShowBy && (
            <Box
              justifyContent={'space-between'}
              alignItems={'center'}
              display={'flex'}
              mt={1}
              gap={3}
              role="group"
              aria-label="Show By Filter Options"
              data-test-id="ShowBySection"
            >
              <Box gap={1} display={'flex'} alignItems={'center'}>
                <Typography
                  className="title"
                  variant="subtitle1"
                  data-test-id="ShowByLabel"
                  tabIndex={0}
                  data-testid={`filter-panel-show`}
                >
                  {t('filterPanel.show')}:
                </Typography>
                <ArrTooltipWrapper
                  data-testid="ceh-tooltip-wrapper"
                  title={t('filterPanel.tooltipOfShow.title')}
                  trigger={
                    <div tabIndex={0} data-testid={`filter-panel-div-btn`}>
                      <InfoIcon
                        className="view-user-info-icon"
                        data-testid="ceh-info-icon"
                      />
                    </div>
                  }
                >
                  <div data-testid="ceh-tooltip-content">
                    <p data-testid={`filter-panel-content-text`}>
                      {t('filterPanel.tooltipOfShow.content')}
                    </p>
                  </div>
                </ArrTooltipWrapper>
              </Box>
              <ArrSingleSelectDropdown
                dropdownLabel="Top Value Selection"
                dropdownItems={topValueOptions}
                selectedValues={topValue}
                onSelectionChange={setTopValue}
                sizeVariant="medium"
                dataTestId="asd-TopValueDropdown"
                aria-label="Select Top Value"
              />
              <ArrSingleSelectDropdown
                dropdownLabel="Category Selection"
                dropdownItems={categoryOptions}
                selectedValues={categoryValue}
                onSelectionChange={setCategoryValue}
                sizeVariant="medium"
                dataTestId="asd-CategoryDropdown"
                aria-label="Select Category"
              />
              <Typography
                className="title"
                variant="subtitle1"
                data-test-id="ByLabel"
                data-testid={`filter-panel-by`}
              >
                {t('filterPanel.by')}:
              </Typography>
              <ArrSingleSelectDropdown
                dropdownLabel="Volume Selection"
                dropdownItems={volumeOptions}
                selectedValues={volumeValue}
                onSelectionChange={setVolumeValue}
                sizeVariant="medium"
                dataTestId="asd-VolumeDropdown"
                aria-label="Select Volume"
              />

              <ArrButton
                size="medium"
                disabled={disableApply}
                onClick={handleApplyFilters}
                data-test-id="ApplyFiltersButtonBottom"
                aria-label={t('common.button.apply') + ' show by selection'}
                dataTestId="filter-panel-apply-button"
              >
                {t('common.button.apply')}
              </ArrButton>
            </Box>
          )}
        </Box>
        <Box display={'flex'} mt={1}>
          {!!now && (
            <Typography
              className="data"
              tabIndex={0}
              data-testid={`filter-panel-data-as-of`}
            >
              {t('filterPanel.dataAsOf')}: {now}
            </Typography>
          )}
        </Box>
      </Box>
      <FilterSelectionModal
        anchorElement={filterModalState.anchorEl}
        onClose={modalCloseHandler}
        filterSelectionData={
          filterModalState.selectedFilter ?? ({} as ActiveFilter)
        }
        closeOnEsc
        isUpdateModel={isFilterUpdating}
        onAddToFilters={onAddToFiltersHandler}
      />
    </Box>
  );
};

export default FilterPanel;
